import clsx from 'clsx'
import {
    formatLeadScoreFeatureValue,
    getLeadScoreFeatureDescription,
} from 'constants/leadScoreFeatureDescriptions'
import { utcToLocal } from 'date'

// Warm/hot colors matching the supply panel (getLeadQualityColor in trustoo_pros).
const WARM_COLOR_CLASS = 'text-[#F78F08]'
const HOT_COLOR_CLASS = 'text-[#D92C20]'

interface LeadScoreThreshold {
    threshold: number | null
    service_specific: boolean
}

interface ThresholdProps {
    label: string
    colorClassName: string
    data: LeadScoreThreshold
}

const LeadScoreThresholdPill = ({ label, colorClassName, data }: ThresholdProps) =>
    data.threshold ? (
        <div className={clsx('text-md font-medium', colorClassName)}>
            {label} = {data.threshold}
            <span className="text-text-tertiary-600">
                {' '}
                ({!data.service_specific && 'not '}
                service-specific)
            </span>
        </div>
    ) : null

interface LeadScoreFeature {
    feature_name: string
    feature_value: string
    impact: number
}

interface LeadScoreThresholds {
    warm: LeadScoreThreshold
    hot: LeadScoreThreshold
}

interface Props {
    score?: number | null
    scoredAt?: string | null
    scoringMoment?: string | null
    modelVersion?: string | null
    features?: LeadScoreFeature[]
    thresholds?: LeadScoreThresholds | null
}

/**
 * Shows the current lead score of a request plus the feature attributions that
 * contributed most to it (both positive and negative), with English
 * descriptions. Attributions are optional, so the score can render on its own.
 */
export default function RequestLeadScoreCard({
    score,
    scoredAt,
    scoringMoment,
    modelVersion,
    features = [],
    thresholds,
}: Props) {
    if (score === null || score === undefined) {
        return <em className="text-text-tertiary-600">No lead score yet</em>
    }

    const meta = [
        scoringMoment,
        scoredAt ? `scored ${utcToLocal(scoredAt, 'dd MMM HH:mm')}` : null,
        modelVersion,
    ].filter(Boolean)

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-baseline gap-2">
                <span className="text-[32px] font-bold leading-none">{score}</span>
                <span className="text-text-tertiary-600">/ 100</span>
            </div>
            {thresholds && (
                <div>
                    <LeadScoreThresholdPill
                        label="Warm threshold (at creation)"
                        colorClassName={WARM_COLOR_CLASS}
                        data={thresholds.warm}
                    />
                    <LeadScoreThresholdPill
                        label="Hot threshold"
                        colorClassName={HOT_COLOR_CLASS}
                        data={thresholds.hot}
                    />
                </div>
            )}
            {meta.length > 0 && <div className="text-text-tertiary-600">{meta.join(' · ')}</div>}
            {features.length > 0 && (
                <div className="flex flex-col">
                    {features.map((feature) => {
                        const description = getLeadScoreFeatureDescription(
                            feature.feature_name,
                            feature.feature_value,
                        )
                        const isPositive = feature.impact >= 0

                        return (
                            <div
                                key={feature.feature_name}
                                className="flex justify-between gap-2 py-1.5 border-t border-solid border-grey">
                                <div>
                                    <div>
                                        <code>{feature.feature_name}</code>
                                        <span className="text-text-tertiary-600">
                                            {' = '}
                                            {formatLeadScoreFeatureValue(
                                                feature.feature_name,
                                                feature.feature_value,
                                            )}
                                        </span>
                                    </div>
                                    {description && (
                                        <div className="text-text-tertiary-600">
                                            {!isPositive && 'If positive: '}
                                            {description}
                                        </div>
                                    )}
                                </div>
                                <b
                                    className={
                                        isPositive ? 'text-secondary-600' : 'text-churned-red'
                                    }>
                                    {isPositive ? '+' : ''}
                                    {feature.impact}
                                </b>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
